import React from "react";
import { Loader2, ShieldCheck, Crown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

/**
 * Confirmation for the two role changes that used to fire on a single click: promoting a
 * member to Admin (or demoting one) and transferring ownership of the Group.
 *
 * Transfer is the irreversible one — the current Owner becomes an Admin and cannot take the
 * Group back on their own — so it asks for an explicit acknowledgement as the second step.
 * Promotion and demotion are reversible, so one deliberate confirmation is enough.
 */
export function ConfirmMemberActionDialog({ action, onOpenChange, onConfirm }) {
  const [ack, setAck] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const kind = action?.kind;
  const name = action?.name || "this member";

  React.useEffect(() => {
    setAck(false);
    setBusy(false);
  }, [action?.userId, kind]);

  const copy = {
    promote: {
      icon: ShieldCheck,
      title: `Make ${name} an admin?`,
      description:
        "Admins can approve or decline join requests, invite people, manage streams, pin "
        + "posts, moderate reports and remove or ban members. They cannot delete the group or "
        + "change ownership.",
      cta: "Make admin",
      done: "Promoted to admin.",
    },
    demote: {
      icon: ShieldCheck,
      title: `Remove ${name}'s admin access?`,
      description:
        "They stay a member and keep their posts, but lose every organizer permission "
        + "immediately. You can promote them again at any time.",
      cta: "Remove admin access",
      done: "Admin access removed.",
    },
    transfer: {
      icon: Crown,
      title: `Transfer ownership to ${name}?`,
      description:
        "This cannot be undone by you. They become the group's Owner with full control, "
        + "including deleting the group, and you become an Admin. Only the new Owner can "
        + "transfer it back.",
      cta: "Transfer ownership",
      done: "Ownership transferred.",
    },
  }[kind];

  if (!copy) return null;
  const Icon = copy.icon;
  const needsAck = kind === "transfer";

  return (
    <Dialog open={!!kind} onOpenChange={(v) => !busy && onOpenChange(v)}>
      <DialogContent data-testid={`confirm-${kind}-dialog`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon className="h-4 w-4 text-muted-foreground" />
            {copy.title}
          </DialogTitle>
          <DialogDescription data-testid={`confirm-${kind}-description`}>
            {copy.description}
          </DialogDescription>
        </DialogHeader>

        {needsAck ? (
          <label className="flex cursor-pointer items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm">
            <Checkbox checked={ack} onCheckedChange={(v) => setAck(!!v)}
                      data-testid="confirm-transfer-ack" className="mt-0.5" />
            <span>
              I understand I will no longer be the Owner of this group and cannot reverse this
              myself.
            </span>
          </label>
        ) : null}

        <DialogFooter>
          <Button variant="ghost" disabled={busy} data-testid={`confirm-${kind}-cancel`}
                  onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant={needsAck ? "destructive" : "default"}
                  disabled={busy || (needsAck && !ack)}
                  data-testid={`confirm-${kind}-submit`}
                  onClick={async () => {
                    setBusy(true);
                    try {
                      await onConfirm(copy.done);
                    } finally {
                      setBusy(false);
                    }
                  }}>
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {copy.cta}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
